/**
 * 注册流程辅助弹窗动作 Hook
 * - 人工审核申请提交、多重身份合并、客户档案绑定确认
 */
import { useRegisterStore } from '../../../../../stores/registerStore';
import type { RegisterFormData } from '../../../../../stores/registerStore';
import type { MergeSmsVerification } from '../../../../../api/register';
import type { ToastType } from '../types';

export function useModalActions(
  formData: RegisterFormData,
  showToast: (message: string, type?: ToastType) => void,
  handleClose: () => void,
) {
  // 从store获取状态和动作（与主控制器共享同一 zustand 实例）
  const {
    manualApplicationReason,
    selectedPrimaryIdentity,
    systemCheckResult,
    setShowManualApplication,
    setShowIdentityMerge,
    setShowCustomerBinding,
    setStep,
  } = useRegisterStore();

  // 提交人工申请
  const handleSubmitManualApplication = async () => {
    if (!manualApplicationReason.trim()) {
      showToast('请填写申请原因', 'info');
      return;
    }

    try {
      const { submitManualApplication } = useRegisterStore.getState();
      await submitManualApplication();

      showToast('人工申请已提交，请等待审核', 'success');
      setShowManualApplication(false);
      handleClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : '提交失败，请稍后重试', 'error');
    }
  };

  // 合并身份（verifications：仅号码命中次档的短信验证结果，无待验证项时空数组）。
  // 服务端判定仍存在需短信验证的次档时（success:false + requireSmsVerify），
  // 返回 { requireSmsVerify } 供合并弹窗进入验证子流程；其余失败 toast 后抛出由调用方感知。
  const handleMergeIdentities = async (
    verifications: MergeSmsVerification[],
  ): Promise<{ requireSmsVerify?: Array<{ secondaryId: string; phone: string }> } | undefined> => {
    if (!selectedPrimaryIdentity) {
      showToast('请选择主身份', 'info');
      return;
    }

    try {
      const { mergeIdentities } = useRegisterStore.getState();
      await mergeIdentities(verifications);

      showToast('身份合并成功，请设置密码完成注册', 'success');
      setShowIdentityMerge(false);
      // 合并后信息步的三要素验证仍有效，直接进入密码步提交注册（升格分支自动关联主档）
      setStep(4);
    } catch (error) {
      const requireSmsVerify = (
        error as { data?: { requireSmsVerify?: Array<{ secondaryId: string; phone: string }> } }
      )?.data?.requireSmsVerify;
      if (requireSmsVerify?.length) {
        showToast('存在仅凭联系方式匹配的档案，需短信验证后方可合并', 'info');
        return { requireSmsVerify };
      }
      showToast(error instanceof Error ? error.message : '身份合并失败，请稍后重试', 'error');
      throw error;
    }
  };

  // 合并人工复议：无法接收验证码或对合并有疑问 → 关闭合并向导，预填复议原因打开人工申请
  // 提交申请后走 handleSubmitManualApplication 的统一收尾（关闭注册弹窗，等待审核结果）
  const handleMergeAppeal = () => {
    const { setManualApplicationReason, setShowManualApplication } = useRegisterStore.getState();
    setShowIdentityMerge(false);
    setManualApplicationReason(
      '身份合并复议：系统检测到联系方式相同但姓名不同的档案，短信验证无法完成（或对合并结果有疑问），申请人工审核档案归属后再合并。'
    );
    setShowManualApplication(true);
  };

  // 确认客户绑定（认领确认页「确认识领」）
  // 认领统一在提交注册时由后端认领模式完成（customer_id → 档案升格：注册信息全量替换、
  // 不新增用户），此处仅引导用户进入密码步/提交步，避免绑定请求缺邮箱验证码而失败。
  const handleConfirmCustomerBinding = async () => {
    const matchedCustomer =
      systemCheckResult?.matchedEntity ?? systemCheckResult?.matchedEntities?.[0];
    if (!matchedCustomer?.id) {
      showToast('客户信息不存在', 'error');
      return;
    }

    setShowCustomerBinding(false);
    if (!formData.username || !formData.password) {
      showToast('请先设置密码，提交注册时将自动认领该客户档案', 'info');
    } else {
      showToast('已确认认领该档案，请提交注册完成升格', 'success');
    }
    setStep(4);
  };

  return {
    handleSubmitManualApplication,
    handleMergeIdentities,
    handleMergeAppeal,
    handleConfirmCustomerBinding,
  };
}